import type { Project } from '@/domain/types';

/**
 * The command layer.
 *
 * Every state change in the DAW that a user could undo goes through a command:
 * a plain `{ type, params }` record that is validated before it runs. Nothing
 * outside this layer mutates a project.
 *
 * Three properties fall out of that:
 *
 *  - Undo is uniform. Commands are pure functions `Project -> Project` built on
 *    structurally-shared updates, so the stack stores project references.
 *  - Commands are serialisable, which is what a future scripting or AI layer
 *    needs: it can only ever *propose* a `{type, params}` record, which is then
 *    validated by `parse` exactly like input from a file. There is no path from
 *    a generated string to arbitrary internal mutation.
 *  - Continuous gestures (dragging a fader) coalesce into one history entry via
 *    `coalesceKey`, instead of flooding the stack with a hundred micro-edits.
 */

export interface CommandRecord {
  type: string;
  params: Record<string, unknown>;
}

export interface CommandDefinition<P> {
  type: string;
  /** Human-readable history entry, e.g. "Move 3 clips". */
  label: (params: P, project: Project) => string;
  /** Validates untrusted input. Returns null to reject the command outright. */
  parse: (raw: Record<string, unknown>) => P | null;
  apply: (project: Project, params: P) => Project;
  /**
   * Non-null when consecutive commands with the same key should merge into a
   * single undo step (fader drags, clip drags, typing a track name).
   */
  coalesceKey?: (params: P) => string | null;
}

/** Type-erased definition, used only inside the registry. */
export interface ErasedCommandDefinition {
  type: string;
  label: (params: unknown, project: Project) => string;
  parse: (raw: Record<string, unknown>) => unknown;
  apply: (project: Project, params: unknown) => Project;
  coalesceKey?: (params: unknown) => string | null;
}

export class CommandError extends Error {
  constructor(
    readonly commandType: string,
    message: string,
  ) {
    super(message);
    this.name = 'CommandError';
  }
}
